import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useHookFormMask } from "use-mask-input";
import { toast } from "react-toastify";

import "../../../../components/forms/forms.css";
import styles from "./mantenedoraUserEdit.module.css";
import Button from "../../../../components/button/button";
import HeaderTitle from "../../../../components/header/headerTitle/headerTitle";
import ErrorMessage from "../../../../components/forms/errorMessage/errorMessage";
import Badge from "../../../../components/badge/badge";
import ArrowLeft from "../../../../assets/icons/arrow-left.svg";
import CheckedCircle from "../../../../assets/icons/checked-circle.svg";
import UserDelete from "../../../../assets/icons/user-delete.svg";
import { PHONE_REGEX } from "../../../../utils/pagesMapper";
import { useMantenedora } from "../../../../context/MantenedoraContext";
import {
  readMantenedoraUsers,
  updateMantenedoraUser,
  deleteMantenedoraUser,
} from "../../../../services/mantenedora";

// Lista única com TODAS as roles disponíveis. Cada role declara seu escopo
// (mantenedora vs institution) — isso controla qual seção de "Acesso" é exibida.
const ALL_ROLES = [
  {
    value: "MantenedoraAdmin",
    label: "Admin da Mantenedora",
    description: "Controle total da mantenedora e de todas as instituições.",
    scope: "mantenedora",
  },
  {
    value: "MantenedoraManager",
    label: "Gerente da Mantenedora",
    description:
      "Acesso de gestão à mantenedora, sem permissões administrativas.",
    scope: "mantenedora",
  },
  {
    value: "Admin",
    label: "Administrador",
    description: "Controle total da instituição.",
    scope: "institution",
  },
  {
    value: "Manager",
    label: "Gerente",
    description: "Acesso de gestão sem permissões administrativas.",
    scope: "institution",
  },
  {
    value: "Operator",
    label: "Operador",
    description: "Acesso a importação e operações do dia a dia.",
    scope: "institution",
  },
  {
    value: "Financial",
    label: "Financeiro",
    description: "Acesso aos dados financeiros.",
    scope: "institution",
  },
  {
    value: "Academic",
    label: "Acadêmico",
    description: "Acesso aos dados acadêmicos.",
    scope: "institution",
  },
];

const ROLE_LABELS = ALL_ROLES.reduce((acc, r) => {
  acc[r.value] = r.label;
  return acc;
}, {});

const isMantenedoraRole = (role) =>
  ALL_ROLES.find((r) => r.value === role)?.scope === "mantenedora";

const MantenedoraUserEdit = () => {
  const { userId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { mantenedora } = useMantenedora();

  const [currentUser, setCurrentUser] = useState(
    location.state?.user || null
  );
  const [originalScope, setOriginalScope] = useState(
    location.state?.scope || null
  );
  const [institutions, setInstitutions] = useState(
    location.state?.institutions || []
  );
  const [selectedInstitutionIds, setSelectedInstitutionIds] = useState(
    Array.isArray(location.state?.user?.institutionAccess)
      ? location.state.user.institutionAccess.map((i) => i.institutionId)
      : []
  );
  const [selectedInstitutionId, setSelectedInstitutionId] = useState(
    location.state?.user?.institutionId || ""
  );
  const [selectedRole, setSelectedRole] = useState(
    location.state?.user?.role || ""
  );
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(!location.state?.user);

  const isTargetMantenedora = isMantenedoraRole(selectedRole);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      firstName: currentUser?.firstName || "",
      lastName: currentUser?.lastName || "",
      email: currentUser?.email || "",
      phoneNumber: currentUser?.phoneNumber || "",
    },
  });

  const registerWithMask = useHookFormMask(register);

  useEffect(() => {
    const shouldFetch = !currentUser && mantenedora?.id && userId;
    if (!shouldFetch) {
      return;
    }

    let cancelled = false;
    const fetch = async () => {
      try {
        setBootstrapping(true);
        const data = await readMantenedoraUsers(mantenedora.id);

        const fromMantenedora = (data?.mantenedoraUsers || []).find(
          (u) => u.id === userId
        );
        if (fromMantenedora) {
          if (cancelled) return;
          setCurrentUser(fromMantenedora);
          setOriginalScope("mantenedora");
          setInstitutions(data?.institutionUsers || []);
          setSelectedRole(fromMantenedora.role || "");
          setSelectedInstitutionIds(
            (fromMantenedora.institutionAccess || []).map(
              (i) => i.institutionId
            )
          );
          setSelectedInstitutionId("");
          return;
        }

        for (const group of data?.institutionUsers || []) {
          const found = group.users.find((u) => u.id === userId);
          if (found) {
            if (cancelled) return;
            setCurrentUser({ ...found, institutionId: group.institutionId });
            setOriginalScope("institution");
            setInstitutions(data?.institutionUsers || []);
            setSelectedRole(found.role || "");
            setSelectedInstitutionId(group.institutionId);
            setSelectedInstitutionIds([]);
            return;
          }
        }

        if (!cancelled) {
          toast.error("Usuário não encontrado.");
          navigate("/app/gestao-usuarios", { replace: true });
        }
      } catch (error) {
        if (!cancelled) {
          toast.error("Erro ao carregar usuário.");
          console.error(error);
          navigate("/app/gestao-usuarios", { replace: true });
        }
      } finally {
        if (!cancelled) {
          setBootstrapping(false);
        }
      }
    };

    fetch();
    return () => {
      cancelled = true;
    };
  }, [currentUser, mantenedora?.id, userId, navigate]);

  useEffect(() => {
    if (currentUser) {
      reset({
        firstName: currentUser.firstName || "",
        lastName: currentUser.lastName || "",
        email: currentUser.email || "",
        phoneNumber: currentUser.phoneNumber || "",
      });
    }
  }, [currentUser, reset]);

  const handleInstitutionToggle = (institutionId) => {
    setSelectedInstitutionIds((prev) =>
      prev.includes(institutionId)
        ? prev.filter((id) => id !== institutionId)
        : [...prev, institutionId]
    );
  };

  const handleSelectAllInstitutions = () => {
    if (selectedInstitutionIds.length === institutions.length) {
      setSelectedInstitutionIds([]);
    } else {
      setSelectedInstitutionIds(institutions.map((i) => i.institutionId));
    }
  };

  const onSubmit = async (data) => {
    if (!selectedRole) {
      toast.error("Selecione um perfil para o usuário.");
      return;
    }

    if (!isTargetMantenedora && !selectedInstitutionId) {
      toast.error("Selecione uma instituição para o usuário.");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phoneNumber: data.phoneNumber || null,
        role: selectedRole,
      };

      if (isTargetMantenedora) {
        payload.institutionIds = selectedInstitutionIds;
      } else {
        payload.institutionId = selectedInstitutionId;
      }

      await updateMantenedoraUser(mantenedora.id, currentUser.id, payload);
      toast.success("Usuário atualizado com sucesso!");
      navigate("/app/gestao-usuarios");
    } catch (error) {
      toast.error(error.message || "Erro ao atualizar usuário.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!currentUser?.id) return;

    const fullName = `${currentUser.firstName || ""} ${
      currentUser.lastName || ""
    }`.trim();
    const confirmed = window.confirm(
      `Tem certeza que deseja excluir o usuário ${
        fullName || currentUser.email
      }? Esta ação não pode ser desfeita.`
    );
    if (!confirmed) return;

    try {
      setDeleting(true);
      await deleteMantenedoraUser(mantenedora.id, currentUser.id);
      toast.success("Usuário excluído com sucesso!");
      navigate("/app/gestao-usuarios");
    } catch (error) {
      toast.error(error.message || "Erro ao excluir usuário.");
    } finally {
      setDeleting(false);
    }
  };

  if (bootstrapping || !currentUser) {
    return (
      <div>
        <HeaderTitle title="Editar Usuário">
          <Link to="/app/gestao-usuarios">
            <Button
              icon={ArrowLeft}
              text="Voltar"
              size="normal"
              variation="outline"
            />
          </Link>
        </HeaderTitle>
        <div className="mainContentSpace">
          <p>Carregando usuário...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <HeaderTitle title="Editar Usuário">
        <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
          <Link to="/app/gestao-usuarios">
            <Button
              icon={ArrowLeft}
              text="Voltar"
              size="normal"
              variation="outline"
            />
          </Link>
          <Button
            icon={UserDelete}
            text="Excluir Usuário"
            size="normal"
            variation="danger"
            loading={deleting}
            onClick={handleDelete}
          />
        </div>
      </HeaderTitle>

      <div className="mainContentSpace">
        <div className="institution-form">
          <form onSubmit={handleSubmit(onSubmit)}>
            <fieldset>
              <legend>Informações do Usuário</legend>

              <div className="form-row" style={{ marginBottom: "16px" }}>
                <div className={styles.currentRoleBanner}>
                  <span className={styles.currentRoleLabel}>
                    Perfil Atual:
                  </span>
                  <Badge
                    text={ROLE_LABELS[currentUser.role] || currentUser.role}
                    color={
                      currentUser.role === "MantenedoraAdmin" ||
                      currentUser.role === "Admin"
                        ? "purple"
                        : "blue"
                    }
                  />
                  <span className={styles.scopeBadge}>
                    {originalScope === "mantenedora"
                      ? "Usuário da Mantenedora"
                      : "Usuário de Instituição"}
                  </span>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    placeholder="Nome"
                    {...register("firstName", {
                      required: "O nome é obrigatório.",
                      minLength: {
                        value: 2,
                        message: "O nome deve ter pelo menos 2 caracteres.",
                      },
                    })}
                  />
                  <ErrorMessage message={errors.firstName?.message} />
                </div>

                <div className="form-group">
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    placeholder="Sobrenome"
                    {...register("lastName", {
                      required: "O sobrenome é obrigatório.",
                      minLength: {
                        value: 2,
                        message:
                          "O sobrenome deve ter pelo menos 2 caracteres.",
                      },
                    })}
                  />
                  <ErrorMessage message={errors.lastName?.message} />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <input
                    type="text"
                    id="phoneNumber"
                    name="phoneNumber"
                    placeholder="Telefone"
                    {...registerWithMask(
                      "phoneNumber",
                      ["(99) 99999-9999"],
                      {
                        pattern: {
                          value: PHONE_REGEX,
                          message: "O telefone é inválido.",
                        },
                      }
                    )}
                  />
                  <ErrorMessage message={errors.phoneNumber?.message} />
                </div>
              </div>
            </fieldset>

            <fieldset>
              <legend>Acesso</legend>

              <div className="form-row">
                <div className="form-group">
                  <input
                    type="email"
                    id="email"
                    name="email"
                    placeholder="E-mail"
                    {...register("email", {
                      required: "O e-mail é obrigatório.",
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: "O e-mail é inválido.",
                      },
                    })}
                  />
                  <ErrorMessage message={errors.email?.message} />
                </div>
              </div>
            </fieldset>

            <fieldset style={{ marginTop: "8px" }}>
              <legend>Perfil do Usuário</legend>

              <div className={styles.rolesList}>
                {ALL_ROLES.map((role) => (
                  <div
                    key={role.value}
                    className={`${styles.roleCard} ${
                      selectedRole === role.value ? styles.roleCardSelected : ""
                    }`}
                    onClick={() => setSelectedRole(role.value)}
                  >
                    <input
                      type="radio"
                      name="roleSelection"
                      value={role.value}
                      checked={selectedRole === role.value}
                      onChange={() => setSelectedRole(role.value)}
                      className={styles.roleRadio}
                    />
                    <div className={styles.roleInfo}>
                      <div className={styles.roleTitleRow}>
                        <span className={styles.roleTitle}>{role.label}</span>
                        <span
                          className={`${styles.roleScopeTag} ${
                            role.scope === "mantenedora"
                              ? styles.roleScopeTagMantenedora
                              : styles.roleScopeTagInstitution
                          }`}
                        >
                          {role.scope === "mantenedora"
                            ? "Mantenedora"
                            : "Instituição"}
                        </span>
                      </div>
                      <div className={styles.roleDescription}>
                        {role.description}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </fieldset>

            {institutions.length > 0 && isTargetMantenedora && (
              <fieldset style={{ marginTop: "8px" }}>
                <legend>Acesso às Instituições</legend>
                <p className={styles.institutionsHint}>
                  Selecione as instituições às quais este usuário terá acesso.
                </p>
                <div className={styles.institutionsList}>
                  <label className={styles.selectAllLabel}>
                    <input
                      type="checkbox"
                      checked={
                        selectedInstitutionIds.length === institutions.length
                      }
                      onChange={handleSelectAllInstitutions}
                    />
                    <span className={styles.selectAllText}>
                      Selecionar todas
                    </span>
                  </label>
                  {institutions.map((inst) => (
                    <label
                      key={inst.institutionId}
                      className={styles.checkboxLabel}
                    >
                      <input
                        type="checkbox"
                        checked={selectedInstitutionIds.includes(
                          inst.institutionId
                        )}
                        onChange={() =>
                          handleInstitutionToggle(inst.institutionId)
                        }
                      />
                      <span className={styles.checkboxText}>
                        {inst.institutionName}
                        {inst.city && inst.state && (
                          <span className={styles.checkboxSubtext}>
                            {" "} — {inst.city}, {inst.state}
                          </span>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>
            )}

            {institutions.length > 0 && !isTargetMantenedora && (
              <fieldset style={{ marginTop: "8px" }}>
                <legend>Instituição</legend>
                <p className={styles.institutionsHint}>
                  Selecione a instituição à qual este usuário pertence.
                </p>
                <div className={styles.institutionsList}>
                  {institutions.map((inst) => (
                    <label
                      key={inst.institutionId}
                      className={`${styles.checkboxLabel} ${
                        selectedInstitutionId === inst.institutionId
                          ? styles.checkboxLabelSelected
                          : ""
                      }`}
                    >
                      <input
                        type="radio"
                        name="institutionSelection"
                        value={inst.institutionId}
                        checked={selectedInstitutionId === inst.institutionId}
                        onChange={() =>
                          setSelectedInstitutionId(inst.institutionId)
                        }
                      />
                      <span className={styles.checkboxText}>
                        {inst.institutionName}
                        {inst.city && inst.state && (
                          <span className={styles.checkboxSubtext}>
                            {" "} — {inst.city}, {inst.state}
                          </span>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>
            )}

            <div className="form-actions" style={{ marginTop: "20px" }}>
              <Button
                text="Salvar Alterações"
                variation="primary"
                size="big"
                icon={CheckedCircle}
                loading={loading}
              />
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MantenedoraUserEdit;
